import React, { useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { V2CheckboxGroup } from '@uhg-abyss/web/ui/CheckboxGroup';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { V2FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { V2TextInput } from '@uhg-abyss/web/ui/TextInput';
import { V2DateInput } from '@uhg-abyss/web/ui/DateInput';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { useUser } from '@src/state/user/hooks';
import { ElectronicSignature } from './ElectronicSignature';

const AttestationHeading = styled('p', {
  fontSize: '30px',
  fontWeight: 700,
  color: '$primary1',
});

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const InputWrap = styled('div', {
  width: '100%',
});

const CHECKBOX_CONFIG = [
  {
    id: 'attestation-checkbox-1',
    model: 'attestation.providerAttestCheckbox1',
    label:
      'I attest that I am submitting as an individual and am the sole healthcare professional associated with this billing TIN OR I am submitting on behalf of my group/facility and I am authorized to submit registration information for this billing TIN.',
  },
  {
    id: 'attestation-checkbox-2',
    model: 'attestation.providerAttestCheckbox2',
    label:
      'I agree to notify UnitedHealthcare in writing of any change in the information in this form within 10 business days of such change.',
  },
  {
    id: 'attestation-checkbox-3',
    model: 'attestation.providerAttestCheckbox3',
    label:
      'I agree that the primary servicing address listed for the company is not a P.O. Box or a residential home but a building zoned for a business.',
  },
  {
    id: 'attestation-checkbox-4',
    model: 'attestation.providerAttestCheckbox4',
    label:
      'Provider will not knowingly present or cause to be presented a false or fraudulent claim for payment to Plan, and I will not submit claims with deliberate ignorance or reckless disregard of their truth or falsity.',
  },
  {
    id: 'attestation-checkbox-5',
    model: 'attestation.providerAttestCheckbox5',
    label:
      'None of the owners, practitioners, or providers or employees listed or that are employed by a provider is currently sanctioned, suspended, debarred, or excluded from participation in Medicare, Medicaid, or a federal health care program.',
  },
  {
    id: 'attestation-checkbox-6',
    model: 'attestation.providerAttestCheckbox6',
    label:
      'I certify, represent and warrant that all information provided on this registration form is true, accurate and complete that I am authorized to act on behalf of the provider.',
  },
];

const getFullNameFromOHID = (userInfo) => {
  const first = userInfo?.firstName?.trim() || '';
  const last = userInfo?.lastName?.trim() || '';

  return first && last ? `${first} ${last}` : first || last;
};

export const Attest = () => {
  const form = useFormV2();
  const userInfo = useUser.state();

  useEffect(() => {
    const existingSubmitterName = form.getValues('attestation.submitterName');
    const ohidFullName = getFullNameFromOHID(userInfo);

    if (!existingSubmitterName && ohidFullName) {
      form.setValue('attestation.submitterName', ohidFullName);
    }

    if (userInfo?.email) {
      form.setValue('attestation.emailAddress', userInfo.email);
    }

    form.setValue('attestation.dateSubmitted', dayjs().format('MM/DD/YYYY'));
  }, []);

  const attestationCheckboxes = CHECKBOX_CONFIG.map((checkbox, idx) => ({
    value: String(idx),
    label: checkbox.label,
    id: checkbox.id,
  }));

  return (
    <SectionStack style={{ gap: '24px' }}>
      <V2Card>
        <SectionStack>
          <AttestationHeading>Attestation</AttestationHeading>

          <V2FormProvider state={form}>
            <V2CheckboxGroup
              label="By signing this Provider Registration Form, I understand and agree to the following:"
              model="attestation.providerAttestCheckboxes"
              validators={{
                required: 'All statements must be selected to proceed.',
                validate: (value) =>
                  (value && value.length === attestationCheckboxes.length) ||
                  'All statements must be selected to proceed.',
              }}
            >
              <V2CheckboxGroup.Column>
                {attestationCheckboxes.map((checkbox) => (
                  <V2Checkbox
                    key={checkbox.id}
                    id={checkbox.id}
                    label={checkbox.label}
                    value={checkbox.value}
                    css={{
                      'abyss-checkbox-label': { maxWidth: 'none' },
                    }}
                  />
                ))}
              </V2CheckboxGroup.Column>
            </V2CheckboxGroup>

            <Grid>
              <Grid.Col span="33%">
                <InputWrap>
                  <V2TextInput
                    label="Full name"
                    id="attestation-full-name"
                    model="attestation.submitterName"
                    type="text"
                    formatter={(value) => {
                      return value.replace(
                        /[!@#$%^&*)({}<>,.[\]+_`~=\\|';":/?0-9]/g,
                        ''
                      );
                    }}
                    validators={{
                      required: 'Full name is required.',
                      validate: (value) =>
                        !value?.match(/[a-zA-Z]/g) || value.length < 2
                          ? 'Please enter a valid full name.'
                          : true,
                    }}
                    maxLength={40}
                  />
                </InputWrap>
              </Grid.Col>

              <Grid.Col span="33%">
                <InputWrap>
                  <V2TextInput
                    id="attestation-email-address"
                    model="attestation.emailAddress"
                    label="Email address"
                    type="email"
                    isDisabled
                  />
                </InputWrap>
              </Grid.Col>

              <Grid.Col span="33%">
                <InputWrap>
                  <V2DateInput
                    id="attestation-date-submitted"
                    model="attestation.dateSubmitted"
                    label="Date submitted"
                    isDisabled
                    inputOnly
                  />
                </InputWrap>
              </Grid.Col>
            </Grid>
          </V2FormProvider>
        </SectionStack>
      </V2Card>

      <ElectronicSignature />
    </SectionStack>
  );
};
